/**
 * Coeficiente de correlação intraclasse (ICC) e concordância de Bland-Altman.
 *
 * São as duas ferramentas que a Fase 0 do projeto exige: o ICC diz quais medidas
 * são repetíveis o bastante para entrar no protocolo, e o Bland-Altman valida a
 * fotogrametria contra a medição de contato.
 *
 * Estão aqui, em funções puras testáveis, porque a qualidade do dado é o risco
 * número um do negócio. Se calcular o ICC não for trivial, ele não é calculado —
 * e aí ninguém descobre que uma medida é ruim até o banco estar cheio dela.
 */

export interface Replicata {
  /** Identifica o alvo da medição — normalmente o animal. */
  sujeito: string;
  /** Identifica quem mediu (ou a rodada). */
  avaliador: string;
  valor: number;
}

export interface ResultadoIcc {
  /** ICC(2,1): concordância absoluta, avaliadores tratados como efeito aleatório. */
  icc: number;
  sujeitos: number;
  avaliadores: number;
  observacoes: number;
  /** Repetibilidade insuficiente para a medida entrar no protocolo. */
  interpretacao: 'ruim' | 'razoavel' | 'boa' | 'excelente';
}

const media = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;

/**
 * ICC(2,1) — modelo de efeitos aleatórios de duas vias, concordância absoluta,
 * medida única. É o modelo correto para "vários avaliadores medem os mesmos
 * animais e eu quero saber se uma medição isolada é confiável".
 *
 * Exige o desenho balanceado do teste da Fase 0: todos os avaliadores medem
 * todos os animais.
 */
export function icc21(dados: Replicata[]): ResultadoIcc {
  const sujeitos = [...new Set(dados.map((d) => d.sujeito))];
  const avaliadores = [...new Set(dados.map((d) => d.avaliador))];
  const n = sujeitos.length;
  const k = avaliadores.length;

  if (n < 2 || k < 2) {
    throw new Error('ICC exige pelo menos dois sujeitos e dois avaliadores.');
  }
  if (dados.length !== n * k) {
    throw new Error(
      `desenho desbalanceado: ${dados.length} observacoes para ${n} sujeitos x ${k} avaliadores. ` +
        'O ICC(2,1) exige que todos meçam todos.',
    );
  }

  const valores = dados.map((d) => d.valor);
  const grandeMedia = media(valores);

  const mediaPorSujeito = new Map(
    sujeitos.map((s) => [s, media(dados.filter((d) => d.sujeito === s).map((d) => d.valor))]),
  );
  const mediaPorAvaliador = new Map(
    avaliadores.map((a) => [a, media(dados.filter((d) => d.avaliador === a).map((d) => d.valor))]),
  );

  // Soma de quadrados: entre sujeitos, entre avaliadores, e residual.
  const ssSujeito = k * sujeitos.reduce((acc, s) => acc + (mediaPorSujeito.get(s)! - grandeMedia) ** 2, 0);
  const ssAvaliador = n * avaliadores.reduce((acc, a) => acc + (mediaPorAvaliador.get(a)! - grandeMedia) ** 2, 0);
  const ssTotal = valores.reduce((acc, v) => acc + (v - grandeMedia) ** 2, 0);
  const ssErro = ssTotal - ssSujeito - ssAvaliador;

  const msSujeito = ssSujeito / (n - 1);
  const msAvaliador = ssAvaliador / (k - 1);
  const msErro = ssErro / ((n - 1) * (k - 1));

  const numerador = msSujeito - msErro;
  const denominador = msSujeito + (k - 1) * msErro + (k * (msAvaliador - msErro)) / n;

  // Variância entre sujeitos nula (todos mediram igual) é concordância perfeita,
  // não divisão por zero.
  const icc = denominador === 0 ? 1 : Math.max(0, Math.min(1, numerador / denominador));

  return {
    icc: Number(icc.toFixed(4)),
    sujeitos: n,
    avaliadores: k,
    observacoes: dados.length,
    interpretacao: interpretarIcc(icc),
  };
}

/** Faixas usuais (Koo & Li, 2016). Abaixo de 0,75 a medida nao entra no protocolo. */
export const interpretarIcc = (icc: number): ResultadoIcc['interpretacao'] =>
  icc < 0.5 ? 'ruim' : icc < 0.75 ? 'razoavel' : icc < 0.9 ? 'boa' : 'excelente';

/**
 * ICC(1,1) — efeitos aleatorios de UMA via, medida unica. E o modelo certo
 * quando cada sujeito e medido por avaliadores diferentes — a dupla medicao da
 * rotina, em que o par de avaliadores muda de ficha para ficha e nao ha como
 * separar o efeito do avaliador. `grupos` sao as medidas de cada sujeito, todas
 * com o mesmo numero de repeticoes.
 */
export function icc11(grupos: number[][]): ResultadoIcc {
  const n = grupos.length;
  const k = grupos[0]?.length ?? 0;
  if (n < 2 || k < 2) throw new Error('ICC exige pelo menos dois sujeitos com duas medidas cada.');
  if (grupos.some((g) => g.length !== k)) throw new Error('ICC(1,1) exige o mesmo numero de medidas por sujeito.');

  const todos = grupos.flat();
  const grandeMedia = media(todos);
  const medias = grupos.map(media);
  const ssEntre = k * medias.reduce((acc, m) => acc + (m - grandeMedia) ** 2, 0);
  const ssDentro = grupos.reduce((acc, g, i) => acc + g.reduce((a, v) => a + (v - medias[i]!) ** 2, 0), 0);
  const msEntre = ssEntre / (n - 1);
  const msDentro = ssDentro / (n * (k - 1));
  const denominador = msEntre + (k - 1) * msDentro;
  const icc = denominador === 0 ? 1 : Math.max(0, Math.min(1, (msEntre - msDentro) / denominador));

  return {
    icc: Number(icc.toFixed(4)),
    sujeitos: n,
    avaliadores: k,
    observacoes: todos.length,
    interpretacao: interpretarIcc(icc),
  };
}

export interface BlandAltman {
  vies: number;
  desvioPadrao: number;
  limiteInferior: number;
  limiteSuperior: number;
  pares: number;
}

/**
 * Concordância entre dois métodos de medição — na Fase 1, fotogrametria contra
 * medição de contato. Correlação não serve para isso: dois métodos podem estar
 * perfeitamente correlacionados e sistematicamente distantes um do outro.
 */
export function blandAltman(a: number[], b: number[]): BlandAltman {
  if (a.length !== b.length) throw new Error('Bland-Altman exige pares completos.');
  if (a.length < 2) throw new Error('Bland-Altman exige pelo menos dois pares.');

  const diffs = a.map((v, i) => b[i]! - v);
  const vies = media(diffs);
  const variancia = diffs.reduce((acc, d) => acc + (d - vies) ** 2, 0) / (diffs.length - 1);
  const dp = Math.sqrt(variancia);

  const arredonda = (x: number) => Number(x.toFixed(4));
  return {
    vies: arredonda(vies),
    desvioPadrao: arredonda(dp),
    limiteInferior: arredonda(vies - 1.96 * dp),
    limiteSuperior: arredonda(vies + 1.96 * dp),
    pares: diffs.length,
  };
}
